import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { ErrorNote } from '@/components/Layout';

// Test site only: testers whose codes are not reaching them can be given a password instead.
const PASSWORDS_ALLOWED = import.meta.env.VITE_ENVIRONMENT !== 'production';

/** Sign in or sign up with a 6-digit code sent by email. No passwords, and it works on any device. */
export function SignIn() {
  const { session } = useAuth();
  const [params] = useSearchParams();
  // Only same-site paths, so a crafted link can't send people elsewhere after signing in.
  const next = /^\/[a-z0-9/_-]*$/i.test(params.get('next') ?? '') ? params.get('next')! : '/home';
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code' | 'password'>('email');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (session) return <Navigate to={next} replace />;

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: true } });
    setBusy(false);
    if (error) setError(error.status === 429 ? 'Too many attempts. Please wait a minute and try again.' : 'We could not send a code to that address. Please check it and try again.');
    else setStage('code');
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const { error } = await supabase.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'email' });
    setBusy(false);
    if (error) setError('That code is wrong or has expired. Check the latest email, or send a new code.');
  }

  async function passwordSignIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setError(error.status === 429 ? 'Too many attempts. Please wait a minute and try again.' : 'That email and password do not match.');
  }

  return (
    <div className="mx-auto max-w-sm">
      <h1 className="text-2xl font-bold">Sign in or join</h1>
      {stage === 'email' ? (
        <form onSubmit={sendCode} className="mt-6 space-y-4">
          <p className="text-sm text-ink-500">Enter your email and we'll send you a 6-digit code. New to the portal? The same code creates your account.</p>
          <div>
            <label className="label" htmlFor="email">Email address</label>
            <input id="email" type="email" required autoComplete="email" className="input" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
          <button className="btn-primary w-full" disabled={busy}>{busy ? 'Sending…' : 'Send code'}</button>
          {PASSWORDS_ALLOWED && <button type="button" className="btn-ghost w-full" onClick={() => { setStage('password'); setError(''); }}>Test site: sign in with a password</button>}
        </form>
      ) : stage === 'password' ? (
        <form onSubmit={passwordSignIn} className="mt-6 space-y-4">
          <p className="text-sm text-ink-500">For testers who have been given a password. Members on the live site always sign in with a code.</p>
          <div>
            <label className="label" htmlFor="pw-email">Email address</label>
            <input id="pw-email" type="email" required autoComplete="email" className="input" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="password">Password</label>
            <input id="password" type="password" required autoComplete="current-password" className="input" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
          <button className="btn-primary w-full" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
          <button type="button" className="btn-ghost w-full" onClick={() => { setStage('email'); setPassword(''); setError(''); }}>Use a code instead</button>
        </form>
      ) : (
        <form onSubmit={verify} className="mt-6 space-y-4">
          <p className="text-sm text-ink-500">We've sent a code to <strong className="text-ink-900">{email}</strong>. It lasts 10 minutes. Can't see it? Check your spam folder.</p>
          <div>
            <label className="label" htmlFor="code">6-digit code</label>
            <input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required autoFocus
              className="input text-center text-2xl tracking-[0.5em]" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          {error && <ErrorNote>{error}</ErrorNote>}
          <button className="btn-primary w-full" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Sign in'}</button>
          <button type="button" className="btn-ghost w-full" onClick={() => { setStage('email'); setCode(''); setError(''); }}>Use a different email</button>
        </form>
      )}
    </div>
  );
}
